import React, { useState, useEffect } from 'react';
import axios from '../../utils/axiosConfig';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { 
  Download, 
  Search, 
  Building2, 
  Package, 
  FileText,
  ChevronDown,
  ChevronRight,
  Loader2,
  Calendar,
  Hash
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const FornitoriSection = () => {
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedFornitore, setExpandedFornitore] = useState(null);
  const [totale, setTotale] = useState(0);
  const [dataGenerazione, setDataGenerazione] = useState('');

  useEffect(() => {
    loadFornitori();
  }, []);

  const loadFornitori = async () => {
    try {
      setLoading(true);
      console.log('FornitoriSection - Chiamando API...');
      console.log('FornitoriSection - tenant_list:', localStorage.getItem('tenant_list'));
      
      // L'interceptor in axiosConfig.js gestisce automaticamente gli header
      const res = await axios.get(`${API}/gestione/haccp/fornitori/registro`);
      
      console.log('FornitoriSection - Risposta:', res.data);
      
      setFornitori(res.data.fornitori || []);
      setTotale(res.data.totale_fornitori || 0);
      setDataGenerazione(res.data.data_generazione || '');
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
      console.error('Errore dettagli:', error.response?.data);
      
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setDownloading(true);
      // L'interceptor gestisce gli header
      const response = await axios.get(`${API}/gestione/haccp/fornitori/registro-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `Registro_Fornitori_HACCP.pdf`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF scaricato!');
    } catch (error) {
      console.error('Errore download PDF:', error);
      toast.error('Errore nel download del PDF');
    } finally {
      setDownloading(false);
    }
  };

  const filteredFornitori = fornitori.filter(f => 
    f.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.prodotti?.some(p => p.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Caricamento registro fornitori...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4" data-testid="fornitori-haccp-section">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b">
        <div>
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-6 w-6 text-blue-600" />
            Registro Fornitori HACCP
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            <span className="font-semibold text-blue-600">{totale}</span> fornitori qualificati | Aggiornato: {dataGenerazione}
          </p>
        </div>
        <Button
          onClick={downloadPdf}
          disabled={downloading}
          className="bg-blue-600 hover:bg-blue-700 shadow-md"
          data-testid="download-fornitori-pdf"
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Esporta PDF
        </Button>
      </div>

      {/* Info normativa */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm text-blue-800">
          <strong>Reg. CE 852/2004</strong> - Questo registro documenta tutti i fornitori qualificati 
          e i prodotti forniti per garantire la tracciabilità in caso di controllo sanitario.
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Cerca fornitore o prodotto..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
          data-testid="search-fornitori"
        />
      </div>

      {/* Lista Fornitori */}
      <div className="space-y-2">
        {filteredFornitori.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {searchTerm ? 'Nessun fornitore trovato' : 'Nessun fornitore registrato'}
          </div>
        ) : (
          filteredFornitori.map((fornitore, index) => {
            // Database domini noti per aziende italiane famose
            const knownDomains = {
              'marr': 'marr.it',
              'metro': 'metro.it',
              'amazon': 'amazon.it',
              'aruba': 'aruba.it',
              'enel': 'enel.it',
              'telecom': 'telecomitalia.it',
              'tim': 'tim.it',
              'vodafone': 'vodafone.it',
              'wind': 'wind.it',
              'fastweb': 'fastweb.it',
              'eni': 'eni.com',
              'conad': 'conad.it',
              'coop': 'e-coop.it',
              'esselunga': 'esselunga.it',
              'carrefour': 'carrefour.it',
              'lidl': 'lidl.it',
              'eurospin': 'eurospin.it',
              'mediaworld': 'mediaworld.it',
              'unieuro': 'unieuro.it',
              'ikea': 'ikea.com',
              'leroy': 'leroymerlin.it',
              'brico': 'bricoio.it',
              'ferrero': 'ferrero.it',
              'barilla': 'barilla.it',
              'lavazza': 'lavazza.it',
              'illy': 'illy.com',
              'campari': 'campari.com',
              'peroni': 'peroni.it',
              'sanpellegrino': 'sanpellegrino.com',
              'coca': 'coca-cola.com',
              'pepsi': 'pepsi.com',
              'nestle': 'nestle.it',
              'danone': 'danone.it',
              'granarolo': 'granarolo.it',
              'parmalat': 'parmalat.it',
              'galbani': 'galbani.it',
              'mutti': 'mutti-parma.com',
              'divella': 'divella.it',
              'dececco': 'dececco.it',
              'rummo': 'pastarummo.it',
              'antinori': 'antinori.it',
              'frescobaldi': 'frescobaldi.it',
              'zonin': 'zonin.it',
              'masi': 'masi.it',
              'allegrini': 'allegrini.it',
              'cantele': 'cantele.it',
              'ceretto': 'ceretto.com',
              'tasca': 'tascadalmerita.it',
              'zaccagnini': 'zaccagnini.it',
              'anticimex': 'anticimex.it',
              'doreca': 'doreca.it',
            };
            
            // Trova dominio per il logo
            const getLogoDomain = (name) => {
              const lowerName = name.toLowerCase();
              // Cerca match esatto o parziale nei domini noti
              for (const [key, domain] of Object.entries(knownDomains)) {
                if (lowerName.includes(key)) {
                  return domain;
                }
              }
              // Fallback: prova a costruire dominio dal nome
              const cleanName = lowerName
                .replace(/\s*(s\.?r\.?l\.?s?|s\.?p\.?a\.?|s\.?n\.?c\.?|s\.?a\.?s\.?|soc\.?\s*agr\.?|società|azienda|ditta|di\s+\w+)\s*/gi, '')
                .replace(/[^a-z0-9]/g, '')
                .substring(0, 20);
              return cleanName.length > 3 ? `${cleanName}.it` : null;
            };
            
            // Genera colore unico basato sul nome
            const getColorFromName = (name) => {
              const colors = [
                'from-blue-500 to-blue-600',
                'from-emerald-500 to-emerald-600',
                'from-violet-500 to-violet-600',
                'from-amber-500 to-amber-600',
                'from-rose-500 to-rose-600',
                'from-cyan-500 to-cyan-600',
                'from-indigo-500 to-indigo-600',
                'from-teal-500 to-teal-600',
                'from-orange-500 to-orange-600',
                'from-pink-500 to-pink-600',
              ];
              let hash = 0;
              for (let i = 0; i < name.length; i++) {
                hash = name.charCodeAt(i) + ((hash << 5) - hash);
              }
              return colors[Math.abs(hash) % colors.length];
            };
            
            // Estrai iniziali (max 2 lettere)
            const getInitials = (name) => {
              const words = name.split(/\s+/).filter(w => w.length > 0 && !['srl', 'spa', 'snc', 'sas', 'di', 'e', 'srls'].includes(w.toLowerCase()));
              if (words.length >= 2) {
                return (words[0][0] + words[1][0]).toUpperCase();
              }
              return name.substring(0, 2).toUpperCase();
            };
            
            const logoDomain = getLogoDomain(fornitore.nome);
            // Usa Google Favicon API come prima scelta (più affidabile), poi Clearbit
            const logoUrl = logoDomain ? `https://www.google.com/s2/favicons?domain=${logoDomain}&sz=128` : null;
            const logoUrlFallback = logoDomain ? `https://logo.clearbit.com/${logoDomain}` : null;
            const colorClass = getColorFromName(fornitore.nome);
            const initials = getInitials(fornitore.nome);
            
            return (
            <div 
              key={index}
              className="border rounded-lg bg-white overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`fornitore-item-${index}`}
            >
              {/* Header Fornitore */}
              <div 
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => setExpandedFornitore(expandedFornitore === index ? null : index)}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md overflow-hidden ${!logoUrl ? `bg-gradient-to-br ${colorClass}` : 'bg-white border border-gray-200'}`}>
                    {logoUrl ? (
                      <img 
                        src={logoUrl}
                        alt=""
                        className="w-10 h-10 object-contain"
                        onError={(e) => {
                          // Nasconde l'immagine e mostra le iniziali
                          e.target.parentElement.classList.remove('bg-white', 'border', 'border-gray-200');
                          e.target.parentElement.classList.add('bg-gradient-to-br', ...colorClass.split(' '));
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span className={`text-white font-bold text-lg tracking-tight ${logoUrl ? 'hidden' : ''}`} style={{display: logoUrl ? 'none' : 'flex'}}>{initials}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h4 className="font-semibold text-gray-900 text-base">{fornitore.nome}</h4>
                    {fornitore.partita_iva && (
                      <p className="text-sm text-blue-600 font-mono">P.IVA: {fornitore.partita_iva}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
                      <span className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded">
                        <Package className="h-3 w-3" />
                        {fornitore.num_prodotti} prodotti
                      </span>
                      <span className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded">
                        <FileText className="h-3 w-3" />
                        {fornitore.totale_documenti} documenti
                      </span>
                      {fornitore.ultima_fornitura && (
                        <span className="flex items-center gap-1 text-gray-400">
                          <Calendar className="h-3 w-3" />
                          {fornitore.ultima_fornitura?.substring(0, 10)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center">
                  {expandedFornitore === index ? (
                    <ChevronDown className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-gray-400" />
                  )}
                </div>
              </div>

              {/* Dettaglio Prodotti */}
              {expandedFornitore === index && (
                <div className="border-t bg-slate-50 p-4">
                  {fornitore.indirizzo && (
                    <p className="text-sm text-gray-600 mb-3">
                      <strong>Sede:</strong> {fornitore.indirizzo}
                    </p>
                  )}
                  <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Prodotti forniti:</p>
                  <div className="flex flex-wrap gap-2">
                    {fornitore.prodotti?.slice(0, 30).map((prodotto, pIndex) => (
                      <span 
                        key={pIndex}
                        className="px-2.5 py-1 bg-white border border-gray-200 rounded-md text-xs text-gray-700 shadow-sm"
                      >
                        {prodotto}
                      </span>
                    ))}
                    {fornitore.prodotti?.length > 30 && (
                      <span className="px-2.5 py-1 bg-blue-100 text-blue-700 rounded-md text-xs font-medium">
                        +{fornitore.prodotti.length - 30} altri
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
          })
        )}
      </div>

      {/* Riepilogo */}
      {filteredFornitori.length > 0 && (
        <div className="bg-gray-100 rounded-lg p-3 text-center text-sm text-gray-600">
          Visualizzati {filteredFornitori.length} di {totale} fornitori
        </div>
      )}
    </div>
  );
};

export default FornitoriSection;
